import { useState, useEffect } from "react";

export default function Transactions() {
  const [transactions, setTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(10);
  const [email, setEmail] = useState("");
  const [amount, setAmount] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || "https://didrouteapi.com/api";

  useEffect(() => {
    fetchTransactions();
  }, [currentPage]);

  const fetchTransactions = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("superadminToken");
      if (!token) {
        alert("No authentication token found. Please login again.");
        return;
      }

      const response = await fetch(
        `${API_URL}/wallet/all-transactions?page=${currentPage}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setTransactions(data.transactions);
        setTotalPages(data.totalPages);
      } else {
        throw new Error(data.error || "Failed to fetch transactions");
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
      alert(error.message || "Failed to fetch transactions");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleAddBalance = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("superadminToken");
      if (!token) {
        alert("No authentication token found. Please login again.");
        return;
      }

      const response = await fetch(
        `${API_URL}/wallet/add-balance-by-email/${encodeURIComponent(email)}/${amount}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      fetchTransactions();
      setEmail("");
      setAmount("");
      setIsModalOpen(false);
      alert("Balance added successfully!");
    } catch (error) {
      console.error("Error adding balance:", error);
      alert(error.message || "Access denied. Superadmin rights required.");
    }
  };

  const filteredTransactions = transactions.filter(
    (transaction) =>
      transaction.description
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      transaction.transaction_type
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="bg-white p-2 rounded-lg shadow-lg">
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Add Balance</h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <form onSubmit={handleAddBalance} className="space-y-4">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  User Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                  required
                  placeholder="user@example.com"
                />
              </div>
              <div>
                <label
                  htmlFor="amount"
                  className="block text-sm font-medium text-gray-700"
                >
                  Amount
                </label>
                <input
                  type="number"
                  id="amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                  required
                  step="0.01"
                  min="0.01"
                  placeholder="0.00"
                />
              </div>
              <div className="flex justify-end space-x-3 mt-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                >
                  Add Balance
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="mx-auto">
        <div className="bg-white py-6">
          <div className="px-4 sm:px-4 lg:px-8">
            <div className="sm:flex sm:items-center">
              <div className="sm:flex-auto">
                <h1 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
                  Payment History
                </h1>
              </div>

              <div className="mt-4 sm:mt-0 sm:flex sm:items-center sm:space-x-4">
                <input
                  type="text"
                  placeholder="Search Transactions ..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="block w-full rounded-md border border-gray-700 placeholder:text-gray-900 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-4 py-2"
                />
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="mt-4 sm:mt-0 px-4 py-2 whitespace-nowrap bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  Add Balance
                </button>
              </div>
            </div>
            <div className="mt-4 flow-root">
              <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                  <table className="min-w-full divide-y divide-gray-700">
                    <thead>
                      <tr>
                        <th
                          scope="col"
                          className="py-3.5 pl-4 pr-3 text-left text-xs font-medium text-gray-900 sm:pl-0"
                        >
                          User Name
                        </th>
                        <th
                          scope="col"
                          className="py-3.5 pl-4 pr-3 text-left text-xs font-medium text-gray-900 sm:pl-0"
                        >
                          User Email
                        </th>
                        <th
                          scope="col"
                          className="py-3.5 pl-4 pr-3 text-left text-xs font-medium text-gray-900 sm:pl-0"
                        >
                          Old Balance
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-xs font-medium text-gray-900"
                        >
                          Amount
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-xs font-medium text-gray-900"
                        >
                          New Balance
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-xs font-medium text-gray-900"
                        >
                          Transaction Type
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-xs font-medium text-gray-900"
                        >
                          Description
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-xs font-medium text-gray-900"
                        >
                          Transaction Time
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800">
                      {filteredTransactions.length > 0 ? (
                        filteredTransactions.map((transaction, index) => {
                          const prevBalance = parseFloat(
                            transaction.prev_balance
                          );
                          const amount = parseFloat(transaction.amount);
                          
                          // Calculate new balance purely based on prev_balance and transaction type
                          let newBalance;
                          if (transaction.transaction_type === "CREDIT") {
                            newBalance = prevBalance + amount;
                          } else if (transaction.transaction_type === "DEBIT") {
                            newBalance = prevBalance - amount;
                          } else {
                            newBalance = prevBalance; // Fallback
                          }

                          // Ignore any new_balance coming from the API
                          transaction.new_balance = newBalance; 

                          // For the display, let's adjust the "Amount" column to show action
                          const amountDisplay = transaction.transaction_type === "DEBIT" 
                            ? `-$${amount.toFixed(2)}` 
                            : `+$${amount.toFixed(2)}`;

                          return (
                            <tr key={transaction.id || index}>
                              <td className="whitespace-nowrap py-4 pl-4 pr-3 text-xs text-gray-900 sm:pl-0">
                                {transaction.user_name || "N/A"}
                              </td>
                              <td className="whitespace-nowrap py-4 pl-4 pr-3 text-xs text-gray-900 sm:pl-0">
                                {transaction.user_email || "N/A"}
                              </td>
                              <td className="whitespace-nowrap py-4 pl-4 pr-3 text-xs text-gray-900 sm:pl-0">
                                ${prevBalance.toFixed(2)}
                              </td>
                              {/* Update the amount display */}
                              <td className={`whitespace-nowrap px-3 py-4 text-xs ${
                                transaction.transaction_type === "CREDIT"
                                  ? "text-green-600"
                                  : "text-red-600"
                              }`}>
                                {amountDisplay}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                                ${newBalance.toFixed(2)}
                              </td>
                              <td className={`whitespace-nowrap px-3 py-4 text-xs ${
                                transaction.transaction_type === "CREDIT"
                                  ? "text-green-600"
                                  : "text-red-600"
                              }`}>
                                {transaction.transaction_type || "N/A"}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                                {transaction.description || "N/A"}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                                {new Date(
                                  transaction.created_at
                                ).toLocaleString()}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td
                            colSpan="8"
                            className="text-center py-4 text-gray-500"
                          >
                            No transactions found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between px-6 py-2">
          <div className="flex items-center text-sm text-gray-900">
            <span>
              Page {currentPage} of {totalPages}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-3 py-1 rounded ${
                currentPage === 1
                  ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-3 py-1 rounded ${
                currentPage === totalPages
                  ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
